import { useState, useEffect } from 'react';
import { usePutProfiles } from '@/api/control/control-plane/control-plane';
import type { UpdateProfileRequest, ProfileDto } from '@/api/control/missionImpactAPI.schemas';
import { useRoles } from '@/contexts/RolesContext';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil } from 'lucide-react';
import { useError } from '@/contexts/ErrorContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Props for the EditProfileDialog component
 */
interface EditProfileDialogProps {
  /** The organization ID */
  orgId: string;
  /** The profile to edit */
  profile: ProfileDto;
  /** Callback to refetch profile data */
  onSuccess: () => void;
  /** Optional controlled open state */
  open?: boolean;
  /** Optional controlled open change handler */
  onOpenChange?: (open: boolean) => void;
  /** Optional custom trigger element */
  trigger?: React.ReactNode;
}

/**
 * Dialog for editing profile information
 */
export function EditProfileDialog({
  orgId,
  profile,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
  trigger,
}: EditProfileDialogProps) {
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;
  const [formData, setFormData] = useState<UpdateProfileRequest>({
    name: profile.name || '',
    regionId: profile.regionId || '',
    deleteMode: profile.deleteMode,
  });

  // Update form when profile changes
  useEffect(() => {
    setFormData({
      name: profile.name,
      regionId: profile.regionId,
      deleteMode: profile.deleteMode,
    });
  }, [profile]);

  const { mutate: updateProfile, isPending: isUpdating } = usePutProfiles({
    mutation: {
      onSuccess: () => {
        toast.success(t.profiles.editDialog.updateSuccess);
        setIsOpen(false);
        onSuccess();
      },
      onError: (error) => {
        handleApiError(error, { context: 'EditProfileDialog.updateProfile', profile });
      },
    },
  });

  /**
   * Handles the save action
   */
  const handleSave = () => {
    updateProfile({ orgId, profileId: profile.id, data: formData });
  };

  /**
   * Handles dialog close and resets form
   */
  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset form to current profile values
      setFormData({
        name: profile.name || '',
        regionId: profile.regionId || '',
        deleteMode: profile.deleteMode,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {isAdminOrOperator &&
        (trigger ? (
          <DialogTrigger asChild>{trigger}</DialogTrigger>
        ) : (
          <DialogTrigger asChild>
            <Button variant="outline" className="h-9">
              <Pencil className="mr-2 h-4 w-4" />
              {t.profiles.editDialog.buttonText}
            </Button>
          </DialogTrigger>
        ))}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.profiles.editDialog.title}</DialogTitle>
          <DialogDescription>{t.profiles.editDialog.description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t.profiles.editDialog.name}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => { setFormData({ ...formData, name: e.target.value }); }}
              placeholder={t.profiles.editDialog.namePlaceholder}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="regionId">{t.profiles.editDialog.region}</Label>
            <Input id="regionId" value={formData.regionId} disabled placeholder={t.profiles.editDialog.regionPlaceholder} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { handleClose(false); }}>
            {t.profiles.editDialog.cancel}
          </Button>
          <LoadingButton onClick={handleSave} disabled={!formData.name} isLoading={isUpdating}>
            {t.profiles.editDialog.save}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
